/** @jsxImportSource solid-js */

import { Component, For, createMemo } from 'solid-js';
import { IComment, IUser } from '@tc-comments/providers';
import Comment from '../Comment/Comment';

export interface ICommentsProps {
  readonly currentUser: IUser;
  readonly comments?: readonly IComment[];
  onDeleteComment?(index: number): void;
  onUpdateComment?(index: number, description: string): Promise<boolean>;
  updateSpecificComment?(commentId: string): void;
  readonly isDeleting?: boolean;
  readonly isUpdating?: boolean;
  readonly userRole?: string;
  readonly projectId: string;
  readonly objectId: string | undefined;
  getCommentDetail?(commentDetail: IComment): void;
  readonly isPreviewOn?: boolean;
  readonly projectUser?: readonly IUser[];
  isCommentActionAllowed?(commentAction: string): boolean;
  readonly policyToolTip?: unknown;
}

// Pure presentational component that works with props
const CommentsInternal: Component<ICommentsProps> = (props) => {
  const allComments = createMemo(() => {
    return props.comments || [];
  });

  const sortedComments = createMemo(() => {
    return [...allComments()].sort((a, b) => {
      const dateA = new Date(a.createdOn || '').getTime();
      const dateB = new Date(b.createdOn || '').getTime();
      return dateB - dateA; // Most recent first
    });
  });

  return (
    <div class="comments-container">
      <div class="section-details comments-list px-0">
        <div class="sub-section">
          <div class="section-details">
            <ul class="list">
              <For each={sortedComments()}>
                {(comment, index) => (
                  <Comment
                    currentUser={props.currentUser}
                    commentIndex={index()}
                    comment={comment}
                    onDeleteComment={
                      props.onDeleteComment ||
                      ((): void => {
                        // No-op fallback
                      })
                    }
                    onUpdateComment={
                      props.onUpdateComment ||
                      ((): Promise<boolean> => Promise.resolve(false))
                    }
                    updateSpecificComment={
                      props.updateSpecificComment ||
                      ((): void => {
                        // No-op fallback
                      })
                    }
                    isDeleting={props.isDeleting || false}
                    isUpdating={props.isUpdating || false}
                    userRole={props.userRole || 'USER'}
                    projectId={props.projectId}
                    objectId={props.objectId}
                    getCommentDetail={
                      props.getCommentDetail ||
                      ((): void => {
                        // No-op fallback
                      })
                    }
                    isPreviewOn={props.isPreviewOn || false}
                    projectUser={props.projectUser}
                    isCommentActionAllowed={
                      props.isCommentActionAllowed || (() => true)
                    }
                    policyToolTip={props.policyToolTip}
                  />
                )}
              </For>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Pure presentational component - no providers needed
const Comments: Component<ICommentsProps> = (props) => {
  return <CommentsInternal {...props} />;
};

export default Comments;
